import { faChevronDown, faCheck, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components";
import { User } from "@/pages/Public/pages/Login/schema/login";

import { SendingSeatSlotData, sendingSeatAvailability } from "./sendingSeatAvailability";

export const SendingUserCell: FunctionComponent<{
	row: User;
	slotData?: SendingSeatSlotData | null;
	onToggle: (row: User) => void;
}> = ({ row, slotData, onToggle }) => {
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
		null
	);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const isSending = row.linkedinAllowed;
	// The "is a seat free" rule (including the over-capacity case) lives in one helper, shared with
	// the invite modal, so promoting here and inviting there cannot disagree.
	const isDisabled = !isSending && !!slotData && !sendingSeatAvailability(slotData).hasFreeSeat;
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);

	useEffect(() => {
		if (!open) {
			return;
		}

		const updatePosition = () => {
			if (buttonRef.current) {
				const rect = buttonRef.current.getBoundingClientRect();
				const menuWidth = 260;
				const viewportPadding = 8;
				const overflowsRight = rect.left + menuWidth > window.innerWidth - viewportPadding;
				const left = overflowsRight ? rect.right - menuWidth : rect.left;

				setPosition({
					top: rect.bottom + 4,
					left,
					width: rect.width,
				});
			}
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	return (
		<div className="relative">
			<button
				ref={buttonRef}
				className={classNames(
					"inline-flex items-center justify-between gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors w-full whitespace-nowrap",
					isSending
						? "bg-green-200 border-green-500/30 text-green-700"
						: "bg-gray-100 border-gray-500/30 text-gray-700",
					isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
				)}
				onClick={() => !isDisabled && setOpen((o) => !o)}
			>
				{isSending ? ts("sendingLabel") : ts("nonSendingLabel")}
				{open ? (
					<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
				) : (
					<Icon className="w-2.5 h-2.5" icon={faChevronDown} />
				)}
			</button>

			{open &&
				ReactDOM.createPortal(
					<>
						<div className="fixed inset-0 z-[1999]" onClick={() => setOpen(false)} />
						<div
							className="fixed bg-white border border-border-light rounded-xl shadow-lg z-[2000] w-[260px] overflow-hidden"
							style={
								position ? { top: position.top, left: position.left } : { visibility: "hidden" }
							}
							onClick={(e) => e.stopPropagation()}
						>
							{[
								{
									value: true,
									label: ts("sendingLabel"),
									desc: ts("sendingLabelDesc"),
								},
								{
									value: false,
									label: ts("nonSendingLabel"),
									desc: ts("nonSendingLabelDesc"),
								},
							].map((opt) => (
								<button
									key={String(opt.value)}
									className={classNames(
										"flex flex-col cursor-pointer items-start w-full px-3.5 py-3 text-left border-b border-border-light last:border-0 hover:bg-gray-50/20 transition-colors",
										isSending === opt.value ? "bg-gray-50/30" : "bg-white"
									)}
									onClick={() => {
										if (opt.value !== isSending) {
											onToggle(row);
										}

										setOpen(false);
									}}
								>
									<div className="flex items-center gap-1.5 mb-0.5">
										<span className="text-sm font-medium text-text">{opt.label}</span>
										{isSending === opt.value && (
											<Icon className="w-3 h-3 text-blue-action" icon={faCheck} />
										)}
									</div>
									<span className="text-xs text-text-meta">{opt.desc}</span>
								</button>
							))}
						</div>
					</>,
					document.body
				)}
		</div>
	);
};
